import { z } from 'zod';
import { DocId, IsoTimestamp, TenantId } from '../common.js';
/**
 * Sara batch-transcriptietaak.
 *
 * Firestore path: tenants/{tenantId}/sara_tasks/{taskId}
 *
 * Tenant-scoped subcollectie (hard rule 1): elke taak leeft onder een tenant.
 * `createdByUserId` wordt server-side gestempeld; alleen die gebruiker mag de
 * taak lezen of goedkeuren (toegang-scoping fase 5.1). Andere gebruikers krijgen
 * 404 (deny-by-default), niet 403 — het bestaan van de taak lekt zo niet.
 *
 * De inhoud (audio, transcript, SOEP) verlaat nooit de logs (hard rule 7) en
 * gaat nooit richting een externe API buiten de eigen transcriber/agent-pijplijn.
 *
 * Statusmachine (ADR 0005/0009 + bouwplan fase 4):
 *   pending → transcribing → pseudonymizing → summarizing → pending_approval
 *   → approved
 * elke stap kan naar `failed` vallen.
 */
export const SaraTaskStatus = z.enum([
    'pending',
    'transcribing',
    'pseudonymizing',
    'summarizing',
    'pending_approval',
    'approved',
    'failed',
]);
/**
 * Schrijfstijl van het SOEP-concept, per gebruiker instelbaar (feedback
 * Aysa/Kimberley/Marc 19-8-2026; besluit 20-8-2026). Bewaard op het
 * gebruikersdocument (UserSchema.soepStyle), door de backend op de taak
 * gestempeld bij aanmaak, en door de transcriber gelezen
 * (agents/sara-transcriber/src/soep.py — daar gespiegeld houden).
 * Ontbreekt de waarde, dan geldt 'bondig'.
 */
export const SaraSoepStyle = z.enum(['steekwoorden', 'bondig', 'uitgebreid']);
/**
 * SOEP-concept: Subjectief / Objectief / Evaluatie / Plan. De POH kan elk veld
 * bewerken vóór goedkeuring; velden mogen leeg zijn. 20k tekens per veld is ruim
 * voor een consultsamenvatting en houdt een pathologisch grote payload tegen.
 */
export const SaraSoep = z.object({
    s: z.string().max(20_000),
    o: z.string().max(20_000),
    e: z.string().max(20_000),
    p: z.string().max(20_000),
});
/**
 * Toegestane opnameformaten. Desktopbrowsers nemen op als Opus/WebM (ADR 0009
 * punt 3); Safari op iOS kan geen WebM produceren en levert alleen AAC in een
 * MP4-container. Bewust een gesloten enum: zowel het objectpad als de
 * ondertekende Content-Type worden hieruit afgeleid, dus een willekeurig type
 * mag er nooit in.
 */
export const SaraAudioMimeType = z.enum(['audio/webm', 'audio/mp4']);
/**
 * Hetzelfde contract, maar tolerant voor de codec-parameters die MediaRecorder
 * meegeeft (`audio/mp4;codecs=mp4a.40.2` op iOS, `audio/webm;codecs=opus` op
 * desktop). De signed URL wordt op het BASISTYPE ondertekend, dus normaliseren
 * we hier één keer aan de rand: zo hangt het niet van de client af of de
 * Content-Type straks matcht met de handtekening (anders 403 bij GCS).
 * Alles wat na het strippen niet in de enum zit, blijft een nette 400.
 */
export const SaraAudioMimeTypeInput = z.preprocess((v) => (typeof v === 'string' ? v.split(';')[0].trim().toLowerCase() : v), SaraAudioMimeType);
/**
 * Bestandsextensie van een opname, mechanisch afgeleid uit het subtype achter
 * `audio/`: audio/webm → webm, audio/mp4 → mp4. Backend (objectpad) en frontend
 * (downloadnaam) delen deze regel zodat de extensie nooit geraden of
 * gehardcodeerd wordt — alle opnames van vóór het mobiele pad zijn `.webm` en
 * moeten leesbaar en opruimbaar blijven.
 */
export function saraAudioExtension(mimeType) {
    return mimeType.slice('audio/'.length);
}
/**
 * Objectieve gespreksmetrieken uit de transcriber (ADR 0015): spreektijd,
 * stiltes, tempo en (met diarisatie) spreekverhouding en beurtwisselingen.
 * Bewust GEEN emotie- of sentimentlabels — emotieherkenning uit stem is
 * onder de EU AI Act hoog-risico; interpretatie is aan de POH. Aggregaten
 * zonder gespreksinhoud, veldnamen gespiegeld in
 * agents/sara-transcriber/src/metrics.py (daar synchroon houden).
 */
export const SaraSpeakerMetrics = z.object({
    /** Neutraal label ("Spreker 1"); wie POH of cliënt is, weet het systeem niet. */
    label: z.string().max(40),
    talkSec: z.number().nonnegative(),
    talkPct: z.number().min(0).max(100),
    words: z.number().int().nonnegative(),
});
export const SaraConversationMetrics = z.object({
    audioSec: z.number().nonnegative(),
    speechSec: z.number().nonnegative(),
    silenceSec: z.number().nonnegative(),
    silencePct: z.number().min(0).max(100),
    /** Stiltes ≥ 2 s tussen spraaksegmenten. */
    pauseCount: z.number().int().nonnegative(),
    /** Stiltes ≥ 10 s. */
    longPauseCount: z.number().int().nonnegative(),
    longestPauseSec: z.number().nonnegative(),
    wordsPerMinute: z.number().nonnegative(),
    speakerCount: z.number().int().nonnegative().optional(),
    turnCount: z.number().int().nonnegative().optional(),
    speakers: z.array(SaraSpeakerMetrics).max(10).optional(),
});
export const SaraTaskError = z.object({
    code: z.string().max(100),
    message: z.string().max(2000),
});
/**
 * Volledige taak-doc zoals opgeslagen. De transcriptie/SOEP-velden worden door
 * de transcriber/agent-pijplijn geschreven, niet door de backend-routes.
 */
export const SaraTask = z.object({
    id: DocId,
    tenantId: TenantId,
    createdByUserId: DocId,
    status: SaraTaskStatus.default('pending'),
    mimeType: SaraAudioMimeType,
    durationSec: z.number().int().nonnegative().optional(),
    /**
     * GCS-objectpad in de transient audiobucket: `{tenantId}/{taskId}.{ext}`, met
     * de extensie uit `mimeType` (saraAudioExtension). Server-bepaald.
     */
    audioObjectPath: z.string().max(500).optional(),
    /** Gewenste SOEP-schrijfstijl, gestempeld vanuit de gebruikersvoorkeur. */
    soepStyle: SaraSoepStyle.optional(),
    /** Gepseudonimiseerd transcript (nooit ruw). Zichtbaar voor de POH die opnam. */
    transcriptPseudonymized: z.string().optional(),
    soep: SaraSoep.optional(),
    /** Consult-samenvatting voor de Mila-overdracht (ADR 0006: samenvatting + SOEP). */
    summary: z.string().optional(),
    conversationMetrics: SaraConversationMetrics.optional(),
    error: SaraTaskError.optional(),
    createdAt: IsoTimestamp,
    updatedAt: IsoTimestamp.optional(),
    /** Retentie: aanmaak + 72 uur; de janitor wist de doc daarna (fase 5.1). */
    expiresAt: IsoTimestamp.optional(),
});
/**
 * Wat de GET /tasks/:taskId route teruggeeft. Subset van de doc — geen interne
 * velden (audioObjectPath, trigger-timestamps).
 */
export const SaraTaskView = z.object({
    id: DocId,
    status: SaraTaskStatus,
    transcriptPseudonymized: z.string().optional(),
    soep: SaraSoep.optional(),
    summary: z.string().optional(),
    conversationMetrics: SaraConversationMetrics.optional(),
    error: SaraTaskError.optional(),
    createdAt: IsoTimestamp,
    expiresAt: IsoTimestamp.optional(),
});
/**
 * Item in GET /tasks — alleen metadata, nooit transcript/SOEP-inhoud: de lijst
 * dient om een taak binnen de 72u-retentie terug te vinden en te heropenen.
 */
export const SaraTaskListItem = z.object({
    id: DocId,
    status: SaraTaskStatus,
    durationSec: z.number().int().nonnegative().optional(),
    createdAt: IsoTimestamp,
    expiresAt: IsoTimestamp.optional(),
});
/** Body voor POST /recordings/upload-url. */
export const CreateSaraRecordingInput = z.object({
    // Genormaliseerd naar het basistype: de client mag zijn ruwe
    // MediaRecorder-mimeType sturen, de backend tekent en bewaart het basistype.
    mimeType: SaraAudioMimeTypeInput,
    // nonnegative, niet positive: een sub-seconde opname floort naar 0 en dat
    // is geen reden voor een 400 op de upload.
    durationSec: z
        .number()
        .int()
        .nonnegative()
        .max(24 * 60 * 60)
        .optional(),
});
/** Body voor POST /tasks/:taskId/approve — het (mogelijk bewerkte) SOEP. */
export const ApproveSaraTaskInput = z.object({
    soep: SaraSoep,
});
/**
 * Body voor POST /tasks/:taskId/regenerate — genereer het SOEP opnieuw in de
 * gevraagde stijl, op het bewaarde gepseudonimiseerde transcript (72u).
 */
export const RegenerateSaraTaskInput = z.object({
    soepStyle: SaraSoepStyle,
});
//# sourceMappingURL=sara-task.js.map